/**
 * Number Utilities
 * @file numberUtils.js
 * General-purpose number helper functions.
 */

/**
 * Expands a number from scientific notation to a plain decimal string.
 * @param {number} num - The number to expand.
 * @param {Object} options
 * @param {number} [options.precision] - Optional decimal precision (round to N digits).
 * @param {boolean} [options.trimZeros=true] - Remove trailing zeros after decimal.
 * @returns {string} - Expanded number as string.
 */
export function expandScientific(num, options = {}) {
    const { precision, trimZeros = true } = options;

    if (typeof num !== "number" || !isFinite(num)) return String(num);

    let str = num.toString();

    // Not in scientific notation → return as-is (maybe rounded)
    if (!/e/i.test(str)) {
        if (precision != null) str = num.toFixed(precision);
        return trimZeros && str.includes(".") ? str.replace(/\.?0+$/, "") : str;
    }

    // Split scientific notation and initialize variables
    let [base, exponent] = str.split("e"),
        exp = Number(exponent),
        negative = base.startsWith("-");

    if (negative) base = base.slice(1);

    let [intPart, fracPart = ""] = base.split("."),
        digits = intPart + fracPart,
        result;

    if (exp < 0) {
        result = "0." + "0".repeat(Math.abs(exp) - 1) + digits;
    } else {
        if (exp >= fracPart.length) {
            result = digits + "0".repeat(exp - fracPart.length);
        } else {
            let pos = intPart.length + exp;
            result = digits.slice(0, pos) + "." + digits.slice(pos);
        }
    }

    if (precision != null) result = Number(result).toFixed(precision);
    if (trimZeros && result.includes(".")) result = result.replace(/\.?0+$/, "");

    return negative ? "-" + result : result;
}  
